import { SceneCommon, SCENE_COMMON_TOKEN } from '@cgs/common';
import { FreeSpinsInfoEvent, SpinResponse } from '@cgs/data-access';
import {
  Compatibility,
  Container,
  debounce,
  SceneObject,
  Vector2,
  viewportChangeListener,
} from '@cgs/wge';
import { ResourcesComponent } from '../../../game/components/resources_component';
import { T_ISlotGame, RESOURCES_COMPONENT_TOKEN } from '../../../type_definitions';
import {
  GAME_STATE_MACHINE_PROVIDER_TOKEN,
  IGameStateMachineProvider,
} from '../../../reels_engine/game_components_providers/i_game_state_machine_provider';
import { ISlotGame } from '../../../reels_engine/i_slot_game';
import { GameStateMachine } from '../../../reels_engine/state_machine/game-state-machine';
import {
  CAMERA_CONTROL_COMPONENT,
  CameraControlComponent,
} from '../camera-control-component/camera-control.component';

export const ORIENTATION_HANDLER_COMPONENT_TOKEN = Symbol('ORIENTATION_HANDLER_COMPONENT_TOKEN');

export interface BaseOrientationHandlerComponentParams {
  /** Path passed to sceneFactory.build() for portrait overlay scene */
  portraitScenePath: string;
  /** Path passed to sceneFactory.build() for landscape overlay scene */
  landscapeScenePath: string;
  /** ID of the node in gameNode where portrait/landscape scenes are added */
  sceneHolderId: string;

  /** findAllById key for HUD mode nodes */
  hudModeId: string;
  /** State name switched on hud_mode nodes in portrait */
  hudModePortraitState: string;
  /** State name switched on hud_mode nodes in portrait during free spins */
  hudModePortraitFreeSpinsState: string;
  /** State name switched on hud_mode nodes in landscape */
  hudModeLandscapeState: string;
  /** State name switched on hud_mode nodes in landscape during free spins */
  hudModeLandscapeFreeSpinsState: string;

  /** State name for portrait orientation on orientationStateNodeIds */
  verticalState: string;
  /** State name for landscape orientation on orientationStateNodeIds */
  horizontalState: string;

  /** ID of the node whose *parent* is scaled/positioned for portrait scaling */
  modesScalingNodeId: string;
  /** IDs of nodes whose state machines are switched to vertical/horizontal */
  orientationStateNodeIds: string[];

  /** ID of the node providing the reference width for portrait scale calculation */
  sizeNodeVerticalId: string;
  /** ID of the node providing the reference position for portrait scale calculation */
  iconsHolderId: string;
}

const DEFAULT_ORIENTATION_CONFIG: BaseOrientationHandlerComponentParams = {
  portraitScenePath: 'slot/featureVertical',
  landscapeScenePath: 'slot/featureHorizontal',
  sceneHolderId: 'featureSceneHolder',

  hudModeId: 'hud_mode',
  hudModePortraitState: 'portrait',
  hudModePortraitFreeSpinsState: 'portrait_freespins',
  hudModeLandscapeState: 'landscape',
  hudModeLandscapeFreeSpinsState: 'landscape_freespins',

  verticalState: 'vertical',
  horizontalState: 'horizontal',

  modesScalingNodeId: 'Modes',
  orientationStateNodeIds: ['Modes'],

  sizeNodeVerticalId: 'sizeNodeVertical',
  iconsHolderId: 'icons_holder',
};

export class BaseOrientationHandlerComponent {
  protected readonly portrait: SceneObject | null;
  protected readonly landscape: SceneObject | null;
  protected readonly root: SceneObject;
  protected readonly gameStateMachine: GameStateMachine<SpinResponse>;
  protected readonly cameraControl: CameraControlComponent | null;
  protected readonly config: BaseOrientationHandlerComponentParams;

  constructor(container: Container, config?: Partial<BaseOrientationHandlerComponentParams>) {
    this.config = { ...DEFAULT_ORIENTATION_CONFIG, ...config };

    const resourceComponent = container.resolve<ResourcesComponent>(RESOURCES_COMPONENT_TOKEN);
    this.root = resourceComponent.root;

    this.gameStateMachine = container.resolve<IGameStateMachineProvider>(
      GAME_STATE_MACHINE_PROVIDER_TOKEN
    ).gameStateMachine;

    const sceneCommon = container.resolve<SceneCommon>(SCENE_COMMON_TOKEN);
    this.portrait = sceneCommon.sceneFactory.build(this.config.portraitScenePath);
    this.landscape = sceneCommon.sceneFactory.build(this.config.landscapeScenePath);

    this.cameraControl = container.resolve<CameraControlComponent>(CAMERA_CONTROL_COMPONENT, {
      optional: true,
    });

    if (this.portrait && this.landscape) {
      const gameNode = container.resolve<ISlotGame>(T_ISlotGame).gameNode;
      gameNode.findById(this.config.sceneHolderId)?.addChild(this.portrait);
      gameNode.findById(this.config.sceneHolderId)?.addChild(this.landscape);
    }

    const debouncedResize = debounce(() => this.resize(), 100);

    viewportChangeListener.on(debouncedResize);

    this.gameStateMachine.init.entered.listen(() => this.resize());
    this.gameStateMachine.beginFreeSpinsPopup.leaved.listen(() => this.resize());
    this.gameStateMachine.endFreeSpinsPopup.leaved.listen(() => this.resize());
    this.resize();
  }

  protected resize(): void {
    const modesParent = this.root.findById(this.config.modesScalingNodeId)!.parent!;

    if (Compatibility.isPortrait) {
      if (this.portrait && this.landscape) {
        this.landscape.visible = false;
        this.portrait.visible = true;
      }

      const hudState =
        this.gameStateMachine.curResponse &&
        this.gameStateMachine.curResponse.isFreeSpins &&
        this.gameStateMachine.curResponse.freeSpinsInfo!.event !==
          FreeSpinsInfoEvent.FreeSpinsFinished
          ? this.config.hudModePortraitFreeSpinsState
          : this.config.hudModePortraitState;

      this.root
        .findAllById(this.config.hudModeId)
        .forEach((hudMode) => hudMode.stateMachine!.switchToState(hudState));

      this.config.orientationStateNodeIds.forEach((id) => {
        this.root.findById(id)?.stateMachine!.switchToState(this.config.verticalState);
      });

      const initialSizeX = this.root.findById(this.config.sizeNodeVerticalId)?.size.x as number;
      const initialPosX = this.root.findById(this.config.iconsHolderId)?.parent?.position
        .x as number;
      const initialPosY = this.root.findById(this.config.iconsHolderId)?.parent?.position
        .y as number;
      let windowWidth = window.innerWidth;

      const originalRatio = 1657 / 768;
      const windowRatio = window.innerHeight / window.innerWidth;
      if (windowRatio > originalRatio) {
        windowWidth = 768;
        const scale = windowWidth / initialSizeX;
        const offsetX = initialPosX * (1 - scale);
        const offsetY = initialPosY * (1 - scale);
        modesParent.position = new Vector2(offsetX, offsetY);
        modesParent.scale = new Vector2(scale, scale);
      } else {
        const newWindowWidth = windowWidth * (1657 / window.innerHeight);
        const scale = newWindowWidth / initialSizeX;
        const offsetX = initialPosX * (1 - scale);
        const offsetY = initialPosY * (1 - scale);
        modesParent.position = new Vector2(offsetX, offsetY);
        modesParent.scale = new Vector2(scale, scale);
      }
    } else {
      if (this.portrait && this.landscape) {
        this.landscape.visible = true;
        this.portrait.visible = false;
      }

      const hudState =
        this.gameStateMachine &&
        this.gameStateMachine.curResponse &&
        this.gameStateMachine.curResponse.isFreeSpins &&
        this.gameStateMachine.curResponse.freeSpinsInfo!.event !=
          FreeSpinsInfoEvent.FreeSpinsFinished
          ? this.config.hudModeLandscapeFreeSpinsState
          : this.config.hudModeLandscapeState;

      this.root
        .findAllById(this.config.hudModeId)
        .forEach((hudMode) => hudMode.stateMachine!.switchToState(hudState));

      this.config.orientationStateNodeIds.forEach((id) => {
        this.root.findById(id)?.stateMachine!.switchToState(this.config.horizontalState);
      });

      modesParent.position = new Vector2(0.0, 0.0);
      modesParent.scale = new Vector2(1.0, 1.0);
    }

    if (this.cameraControl) {
      this.cameraControl.updateSceneInitialState(modesParent);
      this.cameraControl.resize();
    }
  }
}
